import React from 'react';
import autobind from 'autobind-decorator';
import { Grid, Cell, Offset } from 'react-ittsu/layout';
import gql from 'fraql';

import BaseServiceModal from '../BaseServiceModal';
import Paragraph from '../Paragraph';
import SectionTitle from '../SectionTitle';
import { ProductCard, Stripe, StripeGroup } from '../../forms';
import { priceItem, applyPrice } from '../../../utils/pricing';
import { specsToItems, itemsToSpecs } from '~/pods/order/utils/services/specs-to-items';
import applyChanges from '~/pods/order/utils/services/specs-to-items/apply-changes';
import { getItemsByService, getFeaturesForProduct, openIntercom, merge } from '~/pods/order/utils';
import { createTranslate, translate as t } from '~/utils/translation';

import serviceImages from '~/pods/order/images/services';


const tt = createTranslate('pods.order.services.model_360.order');
const tm = createTranslate('pods.order.components.service_modal');


class Model360 extends React.Component {

  static fragments = {
    Item: gql`
      fragment _ on Item {
        id
        details {
          ... on Model360Details {
            droneShooting
          }
        }
        product {
          id @client(type: Product)
          custom
          name
          features {
            locale
            content
          }
        }
      }
    `,
  }

  state = {
    custom: null,
    specs: {
      droneShooting: false,
      standard: false,
      premium: false,
    },
    price: undefined,
  };

  componentDidUpdate(prevProps) {
    const { service, estate } = this.props;
    const { service: prevService } = prevProps;
    if (service?.id === 'model360' && ! prevService) {
      const specs = itemsToSpecs({ estate, service });
      const items = getItemsByService(estate).model360 || [];
      const customItem = items?.find((item) => item.product.custom);
      const price = items != null ? items.reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0) : 0;
      this.setState({ specs: merge(this.state.specs, specs), price, custom: customItem ? true : null });
    }
  }

  render() {
    const { onClickClose, isVisible, service, estate, sessionUser } = this.props;
    const { specs: { standard, droneShooting, premium }, custom, price } = this.state;
    const { model360 } = serviceImages;
    const items = getItemsByService(estate).model360 || [];
    const customItem = items.find((item) => item.product.custom);
    const customFeatures = custom !== null ? getFeaturesForProduct(customItem?.product) : null;
    return (
      <BaseServiceModal
        isVisible={isVisible && service?.id === 'model360'}
        title={service?.id ? t(`services.${service.id}`) : ''}
        images={Object.values(model360)}
        price={sessionUser.addCurrency(price)}
        onClickConfirm={this._handleClickConfirm}
        confirmAsUpdate={items?.length > 0}
        disclaimer={{
          label: tt('disclaimer.text'),
          description: tt('disclaimer.info'),
        }}
        priceDisclaimer={tt('price_disclaimer')}
        onClickClose={onClickClose}
        addDisabled={price === 0}>
        <div className="childcontent">
          <Paragraph>
            {tt('description')}
          </Paragraph>
          <SectionTitle>
            {tt('select_type')}
          </SectionTitle>
          <Grid withVGutters withHGutters>
            <Cell size="2of4">
              <ProductCard
                name="standard"
                label={tm('standard')}
                note={tm('from_price', { price: applyPrice({ EUR: 1250, GBP: 1230, USD: 1500 }, sessionUser) })}
                fullHeight
                formComponent="radio"
                nonFeatures={[
                  {
                    text: tt('non_features.first'),
                  },
                ]}
                features={[
                  {
                    text: tt('features.first'),
                  },
                  {
                    text: tt('features.second'),
                  },
                  {
                    text: tt('features.third'),
                  },
                  {
                    text: tt('features.fourth'),
                  },
                  {
                    text: tt('features.fifth'),
                  },
                ]}
                value={standard}
                onChange={this._handleClickOption} />
            </Cell>
            <Cell size="2of4">
              <ProductCard
                name="premium"
                label={tm('premium')}
                note={tm('from_price', { price: applyPrice({ EUR: 2000, GBP: 1800, USD: 2400 }, sessionUser) })}
                fullHeight
                formComponent="radio"
                nonFeatures={[
                  {
                    text: tt('non_premium_features.first'),
                  },
                ]}
                features={[
                  {
                    text: tt('premium_features.first'),
                  },
                  {
                    text: tt('premium_features.second.text'),
                    infoText: tt('premium_features.second.tooltip'),
                  }
                ]}
                value={premium}
                onChange={this._handleClickOption} />
            </Cell>
            <Offset size="1of4" />
            <Cell size="2of4">
              <ProductCard
                name="custom"
                label={custom !== null ? `${customItem?.product.name}` : tm('custom')}
                note={custom !== null ? `${sessionUser.addCurrency(customItem?.price)}` : tm('custom')}
                formComponent="radio"
                fullHeight
                features={customFeatures}
                onChange={this._handleClickCustom}
                value={!! custom} />
            </Cell>
            <Offset size="1of4" />
          </Grid>
          <SectionTitle>
            Environment
          </SectionTitle>
          <StripeGroup>
            <Stripe
              title={tt('drone_shooting')}
              subtitle={tm('separate_service')}
              component="switch"
              features={[{
                text: tt('drone_shooting_description'),
              }]}
              value={droneShooting}
              disabled={! standard && ! premium}
              onChange={() => this.setState({ specs: { ...this.state.specs, droneShooting: ! droneShooting } })} />
          </StripeGroup>
        </div>
      </BaseServiceModal>
    );
  }

  @autobind
  _syncState() {
    const { estate, service } = this.props;
    const { specs } = this.state;
    const changes = specsToItems({ specs, estate, service });
    const updatedEstate = applyChanges(changes, estate);
    const items = getItemsByService(updatedEstate).model360 || [];
    const price = items != null
      ? items
          .map((item) => item.estate != null ? item : { ...item, estate: updatedEstate })
          .reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0)
      : 0;
    this.setState({ price });
    return { changes, updatedEstate, price };
  }

  @autobind
  _handleClickCustom() {
    const { custom } = this.state;
    if (custom === null) {
      openIntercom();
      this._resetSpecs(this._syncState);
    }
    else {
      const newValue = ! custom;
      this._resetSpecs(() => this.setState({ custom: newValue }, this._syncState));
    }
  }

  @autobind
  _resetSpecs(onReset) {
    this.setState({ specs: { standard: false, premium: false, droneShooting: false } }, onReset);
  }

  @autobind
  _handleClickOption(_, key) {
    const { specs } = this.state;
    const { droneShooting } = specs;
    const newSpecs = { ...specs, premium: false, standard: false, [key]: ! specs[key] };
    this.setState({
      specs: {
        ...newSpecs,
        droneShooting: ! newSpecs.standard && ! newSpecs.premium ? false : droneShooting,
      }
    }, this._syncState);
  }

  @autobind
  _handleClickConfirm() {
    const { onModifyService, estate } = this.props;
    const { custom } = this.state;
    const extraRemove = custom === false ? [getItemsByService(estate).model360.find((item) => item.product.custom)] : [];
    const { changes } = this._syncState();
    onModifyService({ ...changes, remove: [ ...changes.remove, ...extraRemove ] });
  }
}


export default Model360;
